import React, { useState, useEffect } from 'react';
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation';
import * as utils from '../common/Utils.js';
import { useConfig } from '../contexts/ConfigContext.js';
import { useAdminGuard } from '../hooks/useAdminGuard.js';

import { collection, getDocs, setDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

const DATE_STEALS_BLOCKS_ADDED = new Date('May 27, 2024');

const AUTO_AWARD_ROLES = [
  { id: 'championship',     label: 'Championship Winner' },
  { id: 'scoring_champ',    label: 'Scoring Champion' },
  { id: 'rebounding_champ', label: 'Rebounding Champion' },
  { id: 'assists_champ',    label: 'Assists Champion' },
  { id: 'steals_champ',     label: 'Steals Champion' },
  { id: 'blocks_champ',     label: 'Blocks Champion' },
];

function idifyName(name) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const AdminAwards = () => {
  const config = useConfig();
  const { currentUser, isReady } = useAdminGuard();
  const SEASON = config.currentSeason;
  const seasons = Array.from({ length: parseInt(SEASON) }, (_, i) => i + 1);

  const [dataReady, setDataReady] = useState(false);
  const [badgeTypes, setBadgeTypes] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [allStats, setAllStats] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [allSeasonTeams, setAllSeasonTeams] = useState([]);
  const [allPlayerBadges, setAllPlayerBadges] = useState({});

  const [selectedSeason, setSelectedSeason] = useState('');
  const [eligiblePlayers, setEligiblePlayers] = useState([]);
  const [playerBadges, setPlayerBadges] = useState({});
  const [seasonDataReady, setSeasonDataReady] = useState(false);

  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newType, setNewType] = useState('award');
  const [newAutoAward, setNewAutoAward] = useState([]);
  const [createStatus, setCreateStatus] = useState('idle');

  const [addSelections, setAddSelections] = useState({});
  const [autoStatus, setAutoStatus] = useState('idle');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // badge id pending deletion

  useEffect(() => {
    if (!isReady) return;
    loadData();
  }, [isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    const [badgeSnap, gamesSnap, statsSnap, playersSnap, stSnap, pvSnap] = await Promise.all([
      getDocs(collection(db, 'badgeTypes')),
      getDocs(collection(db, 'games')),
      getDocs(collection(db, 'gameStats')),
      getDocs(collection(db, 'players')),
      getDocs(collection(db, 'seasonTeams')),
      getDocs(collection(db, 'playerViews')),
    ]);

    setBadgeTypes(badgeSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => a.name.localeCompare(b.name)));

    const games = gamesSnap.docs.map(d => ({ id: d.id, ...d.data(), date: d.data().date.toDate() }));
    setAllGames(games);

    const stats = [];
    statsSnap.forEach(d => {
      const gameID = d.id;
      (d.data().stats || []).forEach(s => stats.push({ ...s, gameID }));
    });
    setAllStats(stats);

    setAllPlayers(playersSnap.docs.map(d => d.data()));
    setAllSeasonTeams(stSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    const pvMap = {};
    pvSnap.forEach(d => { pvMap[d.id] = d.data().badges || []; });
    setAllPlayerBadges(pvMap);

    setDataReady(true);
  }

  useEffect(() => {
    if (!selectedSeason || !dataReady) return;
    setSeasonDataReady(false);
    loadSeasonBadges();
  }, [selectedSeason, dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadSeasonBadges() {
    const forSeason = allSeasonTeams.filter(st => st.season == selectedSeason);
    const playerIDs = [...new Set(forSeason.flatMap(st => st.players || []))];
    const players = playerIDs
      .map(id => allPlayers.find(p => p.id === id))
      .filter(Boolean)
      .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
    setEligiblePlayers(players);

    const badges = {};
    playerIDs.forEach(id => { badges[id] = allPlayerBadges[id] || []; });
    setPlayerBadges(badges);
    setSeasonDataReady(true);
  }

  function toggleNewAutoAward(roleID) {
    setNewAutoAward(prev =>
      prev.includes(roleID) ? prev.filter(r => r !== roleID) : [...prev, roleID]
    );
  }

  async function createBadgeType() {
    if (!newName.trim()) return;
    const id = idifyName(newName);
    setCreateStatus('saving');
    try {
      await setDoc(doc(db, 'badgeTypes', id), {
        name: newName.trim(),
        description: newDesc.trim(),
        type: newType,
        autoAward: newAutoAward,
      });
      setBadgeTypes(prev =>
        [...prev.filter(b => b.id !== id), { id, name: newName.trim(), description: newDesc.trim(), type: newType, autoAward: newAutoAward }]
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setNewName('');
      setNewDesc('');
      setNewType('award');
      setNewAutoAward([]);
      setCreateStatus('saved');
    } catch (err) {
      console.error(err);
      setCreateStatus('error');
    }
  }

  async function confirmDeleteBadgeType() {
    if (!deleteConfirm) return;
    await deleteDoc(doc(db, 'badgeTypes', deleteConfirm));
    setBadgeTypes(prev => prev.filter(b => b.id !== deleteConfirm));
    setDeleteConfirm(null);
  }

  async function writeBadgesForPlayer(playerID, updated) {
    await setDoc(doc(db, 'playerViews', playerID), { badges: updated }, { merge: true });
    setPlayerBadges(prev => ({ ...prev, [playerID]: updated }));
    setAllPlayerBadges(prev => ({ ...prev, [playerID]: updated }));
  }

  async function awardBadge(playerID, badgeID) {
    const season = parseInt(selectedSeason);
    const existing = playerBadges[playerID] || [];
    if (existing.some(b => b.badgeID === badgeID && b.season === season)) return;
    await writeBadgesForPlayer(playerID, [...existing, { badgeID, season }]);
  }

  async function removeBadge(playerID, badgeID, season) {
    const existing = playerBadges[playerID] || [];
    console.log('[removeBadge]', { playerID, badgeID, season, existing });
    const updated = existing.filter(b => !(b.badgeID === badgeID && b.season == season));
    console.log('[removeBadge] updated:', updated);
    await writeBadgesForPlayer(playerID, updated);
  }

  // Look up which badge type is assigned to a given auto-award role
  function badgeIDForRole(roleID) {
    const bt = badgeTypes.find(b => (b.autoAward || []).includes(roleID));
    return bt ? bt.id : null;
  }

  function computeStatLeaders() {
    const regularGames = allGames.filter(g =>
      g.season == selectedSeason && g.type === 'regular_season' && g.status === 'completed'
    );
    const regularGameIDs = new Set(regularGames.map(g => g.id));
    const seasonStats = allStats.filter(s => regularGameIDs.has(s.gameID) && s.status === 'played');

    console.log(`[AutoAward] Season ${selectedSeason}: ${regularGames.length} regular season games, ${seasonStats.length} played stat rows`);

    const playerStatsMap = {};
    for (const s of seasonStats) {
      if (!playerStatsMap[s.playerID]) playerStatsMap[s.playerID] = [];
      playerStatsMap[s.playerID].push(s);
    }

    const entries = Object.entries(playerStatsMap);
    const maxGames = entries.length > 0 ? Math.max(...entries.map(([, s]) => s.length)) : 0;
    const minGames = Math.max(2, Math.floor(maxGames * 0.3));
    console.log(`[AutoAward] ${entries.length} players with stats, max games played: ${maxGames}, min games to qualify: ${minGames}`);

    const qualified = entries.filter(([, s]) => s.length >= minGames);
    console.log(`[AutoAward] ${qualified.length} players qualify:`, qualified.map(([pid, s]) => `${pid} (${s.length}g)`));

    function leaders(statFn, pool = qualified) {
      if (pool.length === 0) return [];
      const avgs = pool.map(([pid, stats]) => ({
        pid,
        avg: stats.reduce((sum, s) => sum + (statFn(s) || 0), 0) / stats.length,
      }));
      avgs.sort((a, b) => b.avg - a.avg);
      console.log(`[AutoAward]   top 5:`, avgs.slice(0, 5).map(a => `${a.pid} ${a.avg.toFixed(1)}`));
      const best = avgs[0].avg;
      if (best <= 0) return [];
      return avgs.filter(a => a.avg === best).map(a => a.pid);
    }

    console.log('[AutoAward] Scoring:');
    const scoring = leaders(s => s.points);
    console.log('[AutoAward] Rebounding:');
    const rebounding = leaders(s => s.rebounds);
    console.log('[AutoAward] Assists:');
    const assists = leaders(s => s.assists);

    const sbGames = regularGames.filter(g => g.date > DATE_STEALS_BLOCKS_ADDED);
    const sbGameIDs = new Set(sbGames.map(g => g.id));
    const sbMap = {};
    for (const s of allStats.filter(s => sbGameIDs.has(s.gameID) && s.status === 'played')) {
      if (!sbMap[s.playerID]) sbMap[s.playerID] = [];
      sbMap[s.playerID].push(s);
    }
    const sbMaxGames = Object.values(sbMap).length > 0 ? Math.max(...Object.values(sbMap).map(s => s.length)) : 0;
    const sbMinGames = Math.max(2, Math.floor(sbMaxGames * 0.3));
    const qualifiedSB = Object.entries(sbMap).filter(([, s]) => s.length >= sbMinGames);
    console.log(`[AutoAward] Steals/blocks: ${sbGames.length} eligible games, ${qualifiedSB.length} qualified players`);

    console.log('[AutoAward] Steals:');
    const steals = leaders(s => s.steals, qualifiedSB);
    console.log('[AutoAward] Blocks:');
    const blocks = leaders(s => s.blocks, qualifiedSB);

    console.log('[AutoAward] Leaders:', { scoring, rebounding, assists, steals, blocks });
    return { scoring, rebounding, assists, steals, blocks };
  }

  const CHAMPIONSHIP_TAGS = new Set(['final', 'finals', 'championship']);

  function getChampionshipWinners() {
    const champGames = allGames.filter(g => {
      if (g.season != selectedSeason || g.type !== 'playoff' || g.status !== 'completed' || !g.playoffTags) return false;
      const tags = Array.isArray(g.playoffTags) ? g.playoffTags : [g.playoffTags];
      return tags.some(t => CHAMPIONSHIP_TAGS.has(t));
    });
    // The championship is always the last playoff game — take the latest by date
    const champGame = champGames.length > 0
      ? champGames.reduce((latest, g) => g.date > latest.date ? g : latest)
      : null;
    console.log('[AutoAward] Championship game candidates:', champGames.map(g => `id=${g.id} tags=${Array.isArray(g.playoffTags) ? g.playoffTags.join(',') : g.playoffTags}`));
    console.log('[AutoAward] Championship game selected:', champGame
      ? `id=${champGame.id} | awayTeamID=${champGame.awayTeamID} | homeTeamID=${champGame.homeTeamID} | winner=${champGame.winner}`
      : 'not found');
    if (!champGame || !champGame.winner) {
      console.log('[AutoAward] No winner found on championship game — skipping');
      return [];
    }

    // Match winner against both teamID (base team ID) and doc id (seasonTeam doc ID)
    // to handle different storage formats across seasons
    const winningSeasonTeam = allSeasonTeams.find(
      st => st.season == selectedSeason &&
            (st.teamID === champGame.winner || st.id === champGame.winner)
    );
    console.log('[AutoAward] Season teams for this season:', allSeasonTeams
      .filter(st => st.season == selectedSeason)
      .map(st => `id=${st.id} teamID=${st.teamID}`)
    );
    console.log('[AutoAward] Winning season team:', winningSeasonTeam
      ? `teamID=${winningSeasonTeam.teamID}, ${(winningSeasonTeam.players || []).length} players`
      : 'NOT FOUND — winner ID did not match any season team'
    );
    return winningSeasonTeam ? (winningSeasonTeam.players || []) : [];
  }

  async function autoAwardBadges() {
    setAutoStatus('running');
    try {
      const season = parseInt(selectedSeason);

      // Badge IDs that are auto-awardable (assigned to at least one role)
      const autoAwardableBadgeIDs = new Set(
        badgeTypes.filter(bt => (bt.autoAward || []).length > 0).map(bt => bt.id)
      );

      // Start from allPlayerBadges so we cover players outside the selected season's
      // eligible list (e.g. championship winners on teams not in the management table)
      const updatedBadges = { ...allPlayerBadges };

      // Step 1: strip all auto-awardable badges for this season from every player
      for (const [playerID, badges] of Object.entries(updatedBadges)) {
        const stripped = badges.filter(
          b => !(b.season === season && autoAwardableBadgeIDs.has(b.badgeID))
        );
        if (stripped.length !== badges.length) {
          updatedBadges[playerID] = stripped;
        }
      }

      // Step 2: compute who earned each auto badge this season
      const leaders = computeStatLeaders();
      const champWinners = getChampionshipWinners();

      const roleResults = {
        championship:     champWinners,
        scoring_champ:    leaders.scoring,
        rebounding_champ: leaders.rebounding,
        assists_champ:    leaders.assists,
        steals_champ:     leaders.steals,
        blocks_champ:     leaders.blocks,
      };

      const toAward = [];
      for (const [roleID, playerIDs] of Object.entries(roleResults)) {
        const badgeID = badgeIDForRole(roleID);
        if (!badgeID) continue;
        for (const playerID of playerIDs) {
          toAward.push({ playerID, badgeID });
        }
      }

      const byPlayer = {};
      for (const { playerID, badgeID } of toAward) {
        if (!byPlayer[playerID]) byPlayer[playerID] = [];
        byPlayer[playerID].push(badgeID);
      }

      // Step 3: award fresh and write only players whose badges changed
      let count = 0;
      const allAffectedPlayerIDs = new Set([
        ...Object.keys(updatedBadges).filter(pid =>
          (allPlayerBadges[pid] || []).some(b => b.season === season && autoAwardableBadgeIDs.has(b.badgeID))
        ),
        ...Object.keys(byPlayer),
      ]);

      for (const playerID of allAffectedPlayerIDs) {
        const before = updatedBadges[playerID] || [];
        let after = [...before];
        for (const badgeID of (byPlayer[playerID] || [])) {
          after.push({ badgeID, season });
          count++;
        }
        await setDoc(doc(db, 'playerViews', playerID), { badges: after }, { merge: true });
        updatedBadges[playerID] = after;
      }

      setPlayerBadges(prev => {
        const next = { ...prev };
        for (const pid of allAffectedPlayerIDs) next[pid] = updatedBadges[pid];
        return next;
      });
      setAllPlayerBadges(prev => ({ ...prev, ...updatedBadges }));
      setAutoStatus(`Done — ${count} badge${count !== 1 ? 's' : ''} awarded`);
    } catch (err) {
      console.error(err);
      setAutoStatus('Error — check console');
    }
  }

  if (!dataReady || currentUser == null) {
    return <div className="Account"><LoadingAnimation /></div>;
  }

  const badgeName = (id) => (badgeTypes.find(b => b.id === id) || {}).name || id;

  return (
    <div>
      <div className="league-content">
        <div className="league-body">
          <p className='league-admin-back-button'><a className='league-link' href="/admin"><b>← Back to Admin Home</b></a></p>
          <p className="league-title-standard-secondary text-align-center">Awards</p>

          {/* ── Awards Overview ── */}
          <p className="league-title-standard-small">Awards by Season</p>
          <div className="league-table-container schedule" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Award</th>
                  {seasons.map(s => <th key={s}>S{s}</th>)}
                </tr>
              </thead>
              <tbody>
                {badgeTypes.length === 0 && (
                  <tr><td colSpan={seasons.length + 1} style={{ color: '#888' }}>No badge types yet.</td></tr>
                )}
                {badgeTypes.map(bt => (
                  <tr key={bt.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{bt.name}</td>
                    {seasons.map(s => {
                      const recipients = Object.entries(allPlayerBadges)
                        .filter(([, badges]) => badges.some(b => b.badgeID === bt.id && b.season === s))
                        .map(([pid]) => allPlayers.find(p => p.id === pid))
                        .filter(Boolean)
                        .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
                      return (
                        <td key={s} style={{ fontSize: 13 }}>
                          {recipients.map(p => (
                            <div key={p.id}>
                              <a href={`/players/${p.id}`} className="unstyled-link">
                                {utils.displayPlayerName(p)}
                              </a>
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Badge Types ── */}
          <p className="league-title-standard-small" style={{ marginTop: 32 }}>Badge Types</p>
          <div className="league-table-container schedule" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {badgeTypes.length === 0 && (
                  <tr><td colSpan={5} style={{ color: '#888' }}>No badge types yet.</td></tr>
                )}
                {badgeTypes.map(b => (
                  <tr key={b.id}>
                    <td><code>{b.id}</code></td>
                    <td>{b.name}</td>
                    <td>{b.description}</td>
                    <td>{b.type}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(b.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Create form */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Name</label>
                <input
                  className="form-control"
                  style={{ width: 180 }}
                  value={newName}
                  onChange={e => { setNewName(e.target.value); setCreateStatus('idle'); }}
                  placeholder="e.g. Season MVP"
                />
                {newName && <small style={{ color: '#888', display: 'block' }}>ID: {idifyName(newName)}</small>}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Description</label>
                <input
                  className="form-control"
                  style={{ width: 260 }}
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="Short description"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Type</label>
                <select className="form-control" style={{ width: 140 }} value={newType} onChange={e => setNewType(e.target.value)}>
                  <option value="award">Award</option>
                  <option value="achievement">Achievement</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Auto-Award Roles</label>
                {AUTO_AWARD_ROLES.map(role => (
                  <label key={role.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={newAutoAward.includes(role.id)}
                      onChange={() => toggleNewAutoAward(role.id)}
                    />
                    {role.label}
                  </label>
                ))}
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <button className="btn btn-dark" onClick={createBadgeType} disabled={!newName.trim()}>
                  Create Badge
                </button>
                {createStatus === 'saved' && <span style={{ color: '#555', marginLeft: 8 }}>Saved</span>}
                {createStatus === 'error' && <span style={{ color: 'red', marginLeft: 8 }}>Error</span>}
              </div>
            </div>
          </div>

          {/* ── Season Awards ── */}
          <p className="league-title-standard-small" style={{ marginTop: 32 }}>Award Badges by Season</p>
          <div className="league-table-container schedule" style={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              <select
                className="form-control"
                style={{ width: 160 }}
                value={selectedSeason}
                onChange={e => { setSelectedSeason(e.target.value); setAutoStatus('idle'); }}
              >
                <option value="">Select season</option>
                {seasons.map(s => (
                  <option key={s} value={s}>Season {s}</option>
                ))}
              </select>

              {selectedSeason && (
                <>
                  <button
                    className="btn btn-dark"
                    onClick={autoAwardBadges}
                    disabled={autoStatus === 'running' || !seasonDataReady}
                  >
                    Auto-Award Computed Badges
                  </button>
                  {autoStatus !== 'idle' && (
                    <span style={{ color: autoStatus.startsWith('Error') ? 'red' : '#555' }}>
                      {autoStatus}
                    </span>
                  )}
                </>
              )}
            </div>

            {selectedSeason && !seasonDataReady && <LoadingAnimation />}

            {selectedSeason && seasonDataReady && (
              <table className="table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Season {selectedSeason} Badges</th>
                    <th>Award Badge</th>
                  </tr>
                </thead>
                <tbody>
                  {eligiblePlayers.map(player => {
                    const badges = (playerBadges[player.id] || []).filter(b => b.season === parseInt(selectedSeason));
                    const selected = addSelections[player.id] || '';
                    return (
                      <tr key={player.id}>
                        <td>
                          <a href={`/players/${player.id}`} className="unstyled-link">
                            {utils.displayPlayerName(player)}
                          </a>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {badges.map((b, i) => (
                              <span
                                key={i}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '2px 8px', background: '#f0f0f0', borderRadius: 12,
                                  fontSize: 13,
                                }}
                              >
                                {badgeName(b.badgeID)}
                                <button
                                  onClick={() => removeBadge(player.id, b.badgeID, b.season)}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#888', fontSize: 15 }}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <select
                              className="form-control form-control-sm"
                              style={{ width: 180 }}
                              value={selected}
                              onChange={e => setAddSelections(prev => ({ ...prev, [player.id]: e.target.value }))}
                            >
                              <option value="">Select badge</option>
                              {badgeTypes.map(bt => (
                                <option key={bt.id} value={bt.id}>{bt.name}</option>
                              ))}
                            </select>
                            <button
                              className="btn btn-sm btn-dark"
                              disabled={!selected}
                              onClick={() => {
                                awardBadge(player.id, selected);
                                setAddSelections(prev => ({ ...prev, [player.id]: '' }));
                              }}
                            >
                              Award
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      {deleteConfirm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: 8, padding: '24px 28px', maxWidth: 420, width: '90%' }}
            onClick={e => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: 12 }}>Delete Badge Type</h5>
            <p style={{ marginBottom: 4 }}>Are you sure you want to delete <strong>{deleteConfirm}</strong>?</p>
            <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>This does not remove badges already awarded to players.</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDeleteBadgeType}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminAwards;
